import {
  ConfigurationRuleScopeEntityType,
  ConfigurationRuleScopeMode,
  RegionResponse,
  StoreResponse,
  StoreTypeResponse,
} from '@ydistri/api-sdk';
import { FilteredEntityIds, FilterRuleScopeArgs, Scope } from './types';
import { numberArrayToObject } from './utils';

/**
 * Check if an entity ID is included in scope based on selection mode
 */
export function isEntityInScope(
  entityId: number,
  selectionMode: ConfigurationRuleScopeMode,
  selectedIds: number[],
): boolean {
  switch (selectionMode) {
    case ConfigurationRuleScopeMode.All:
      return true;
    case ConfigurationRuleScopeMode.Specified:
      return selectedIds.includes(entityId);
    case ConfigurationRuleScopeMode.AllExcept:
      return !selectedIds.includes(entityId);
  }
}

/**
 * Filter entity IDs based on scope configuration
 */
export function filterEntitiesByScope(
  entityIds: number[],
  selectionMode: ConfigurationRuleScopeMode,
  selectedIds: number[],
): number[] {
  switch (selectionMode) {
    case ConfigurationRuleScopeMode.All:
      return entityIds;
    case ConfigurationRuleScopeMode.Specified:
      return entityIds.filter(id => selectedIds.includes(id));
    case ConfigurationRuleScopeMode.AllExcept:
      return entityIds.filter(id => !selectedIds.includes(id));
  }
}

/**
 * Filter entities by scope with type safety
 */
export function filterTypedEntitiesByScope<T extends { id: number }>(
  entities: T[],
  selectionMode: ConfigurationRuleScopeMode,
  selectedIds: number[],
): T[] {
  const filteredIds = new Set(
    filterEntitiesByScope(
      entities.map(e => e.id),
      selectionMode,
      selectedIds,
    ),
  );
  return entities.filter(entity => filteredIds.has(entity.id));
}

/**
 * Filters list entity IDs down to scope definition.
 *
 * @param {Scope} scope - The scope object containing entities and their selected IDs.
 * @param {ConfigurationRuleScopeEntityType} entity - The entity type to filter on.
 * @param {number[]} entityIds - The list of numbers to filter.
 * @returns {number[]} - The filtered list of numbers.
 */
export const filterEntity = (
  scope: Scope,
  entity: ConfigurationRuleScopeEntityType,
  entityIds: number[],
): number[] => {
  return filterEntitiesByScope(
    entityIds,
    scope.entities[entity].selectionOption,
    scope.entities[entity].selectedIds,
  );
};

/**
 * Returns resulting IDs of all entity types for whole scope
 * Basically calls filterEntity for all entity types, but also filters stores based on store types
 *
 * @returns {FilteredEntityIds} - The filtered entity IDs.
 * @param args
 */
export const filterRuleScope = (args: FilterRuleScopeArgs): FilteredEntityIds => {
  const { scope, regionIds, storeIds, storeListObject, storeTypeIds, departmentIds } = args;

  // filter regions based on scope
  const filteredRegionList = filterEntity(
    scope,
    ConfigurationRuleScopeEntityType.Regions,
    regionIds,
  );
  const regionTypeObject = numberArrayToObject(filteredRegionList);

  // filter store types based on scope
  const filteredStoreTypeList = filterEntity(
    scope,
    ConfigurationRuleScopeEntityType.StoreTypes,
    storeTypeIds,
  );
  const storeTypeObject = numberArrayToObject(filteredStoreTypeList);

  //filter stores based on scope AND filtered store types
  const filteredStoreList = filterEntity(
    scope,
    ConfigurationRuleScopeEntityType.Stores,
    storeIds,
  ).filter(sid => {
    const storeTypeId = storeListObject[sid]?.storeType?.id;
    const regionId = storeListObject[sid]?.region?.id;
    return storeTypeId && storeTypeObject[storeTypeId] && regionId && regionTypeObject[regionId];
  });

  //filter departments based on scope
  const filteredDepartmentList = filterEntity(
    scope,
    ConfigurationRuleScopeEntityType.Departments,
    departmentIds,
  );

  return {
    [ConfigurationRuleScopeEntityType.Regions]: filteredRegionList,
    [ConfigurationRuleScopeEntityType.StoreTypes]: filteredStoreTypeList,
    [ConfigurationRuleScopeEntityType.Stores]: filteredStoreList,
    [ConfigurationRuleScopeEntityType.Departments]: filteredDepartmentList,
  };
};

/**
 * return stores that belong to the given regions
 */
export function filterStoresByRegions(
  stores: StoreResponse[],
  regions: RegionResponse[],
): StoreResponse[] {
  const regionIdSet = new Set(regions.map(r => r.id));
  return stores.filter(store => store.region?.id && regionIdSet.has(store.region.id));
}

/**
 * Return store types that have at least one store in the given list
 */
export function filterStoreTypesWithStores(
  storeTypes: StoreTypeResponse[],
  stores: StoreResponse[],
): StoreTypeResponse[] {
  const storeTypeIds = new Set(stores.map(s => s.storeType?.id).filter((id): id is number => !!id));
  return storeTypes.filter(st => storeTypeIds.has(st.id));
}
